import { BuildHtml } from "~/components/BuildHtml";
import { Body } from "~/ui-library/components/Body";
import { Headline, HeadlineProps } from "~/ui-library/components/Headline";
import { isPopulatedString } from "~/lib/shared/isPopulatedString";
import {
  downloadChart,
  generateChartFilename,
} from "~/components/EvervizChart/downloadChart";
import { useDocument } from "~/lib/client/useDocument";
import { IconButton } from "~/ui-library/components/IconButton";
import { ArrowDownTrayIcon, MagnifyingGlassIcon } from "~/ui-library/icons";

type HeaderProps = {
  headline?: string;
  subtitle?: string;
  title?: string;
  headlineId?: string;
  headlineAs?: HeadlineProps["as"];
  headlineLevel: HeadlineProps["level"];
  hideHeadline?: boolean;
  showHeader?: boolean;
  chartContentRef?: React.RefObject<HTMLDivElement>;
  openFullscreen?: () => void;
};

export const EvervizChartHeader = ({
  headline,
  subtitle,
  title,
  headlineId,
  headlineAs,
  headlineLevel,
  hideHeadline,
  showHeader,
  chartContentRef,
  openFullscreen,
}: HeaderProps) => {
  const document = useDocument();
  const handleDownloadChart = async () => {
    const filename = generateChartFilename(headline);
    if (chartContentRef?.current && document) {
      await downloadChart({
        element: chartContentRef.current,
        document,
        filename,
      });
    }
  };

  return (
    <div id="headline-container" className="mb-2 flex w-full flex-col">
      <div className="flex w-full flex-auto items-center justify-end space-x-xs overflow-hidden">
        {openFullscreen && (
          <div className="hidden md:block print:hidden" data-html2canvas-ignore>
            <IconButton
              aria-label="Open chart in fullscreen"
              iconClassName="fill-transparent"
              SvgIcon={MagnifyingGlassIcon}
              onClick={openFullscreen}
              size="large"
              variant="subtle"
            />
          </div>
        )}

        {chartContentRef?.current && (
          <div data-html2canvas-ignore>
            <IconButton
              aria-label="Download chart as PNG"
              SvgIcon={ArrowDownTrayIcon}
              onClick={handleDownloadChart}
              size="default"
              variant="subtle"
            />
          </div>
        )}
      </div>
      {isPopulatedString(headline) && !hideHeadline && showHeader && (
        <>
          <Headline
            as={headlineAs}
            id={headlineId}
            level={headlineLevel}
            className="scroll-mt-xxl pb-xs"
          >
            {headline}
          </Headline>
        </>
      )}
      {isPopulatedString(subtitle) && showHeader && (
        <>
          <Body className="text-body-regular">
            <BuildHtml allowStyles html={subtitle} />
          </Body>
        </>
      )}
      {isPopulatedString(title) && showHeader && (
        <Body bold className="py-xs text-body-regular">
          <BuildHtml allowStyles html={title} />
        </Body>
      )}
    </div>
  );
};
